import React, { useState, useEffect, useLayoutEffect } from 'react';
import uniqid from 'uniqid';

import { Stack } from 'ui/stack';
import { Box } from 'ui/box';

import { Loader } from 'ui/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import PageNav from 'containers/user/learn/components/page-nav/page-nav';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { Typography } from 'ui/typography';

import api from 'api';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';
import { onResize } from 'helpers/resize';
import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

interface ReleaseNoteData {
  name?: string;
  releaseNotesDate?: string;
  content?: Array<{
    id: string | number;
    type: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

interface ProductAnnouncementProps {
  releaseNoteId: string | number;
}

const ProductAnnouncement = ({
  releaseNoteId,
}: ProductAnnouncementProps): React.JSX.Element => {
  const [releaseNote, setReleaseNote] = useState<ReleaseNoteData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isTabletView, setIsTabletView] = useState<boolean>(false);

  const user = useCurrentUser();

  useEffect(() => {
    setLoading(true);
    api
      .getReleaseNote(releaseNoteId, user?.data?.oktaUserId ?? '')
      .then((res) => {
        setReleaseNote(
          (res as { data: { data: { attributes: ReleaseNoteData } } }).data.data
            .attributes
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useLayoutEffect(() => {
    onResize(setIsTabletView);
  }, []);

  return loading ? (
    <Loader {...{ loading }} />
  ) : (
    <ResponsiveWrapper customClass="flex flex-col">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Learn', href: '/learn' },
          {
            label: 'Product Announcements',
            href: '/learn/content/product-announcements',
          },
        ]}
        customClass="!mt-5"
      />
      <Typography
        data-testid="product-announcement-heading"
        variant="h1"
        sx={{ pt: { mobile: 2, xs: 3 }, mb: 1 }}
      >
        {releaseNote.name}
      </Typography>

      <Stack
        direction="row"
        sx={{ alignItems: 'center', color: 'tokens.navy.500', mb: 2 }}
      >
        {releaseNote.releaseNotesDate && (
          <>
            <Icons.Calendar className="h-4 w-4 mr-1" />
            <Box sx={{ fontSize: '0.875rem', lineHeight: '1.25rem' }}>
              {formatDate(releaseNote.releaseNotesDate, 'MMM D, YYYY')}
            </Box>
          </>
        )}
      </Stack>

      {isTabletView && (
        <PageNav content={releaseNote.content} isTabletView={true} />
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          order: 1,
          '@media (min-width: 375px)': { flexDirection: 'column' },
          '@media (min-width: 1280px)': { flexDirection: 'row' },
        }}
      >
        <ResponsiveWrapper customClass="flex flex-col">
          {releaseNote.content?.map((component) =>
            getBlocksFromContent({ ...component, key: uniqid() })
          )}
        </ResponsiveWrapper>
        <Stack>
          {!isTabletView && <PageNav content={releaseNote.content} />}
        </Stack>
      </Box>
    </ResponsiveWrapper>
  );
};

export default ProductAnnouncement;
